import { AWAdvisorFilterableAttribute } from '@blk/aw-bff-typescript-models';
import { isReactSelectOptions } from '@blk/aw-utils-core';
import type { Filter } from '@blk/awt-aladdinwealthserver';
import { AWFilter } from '@blk/awt-userpreference';
import { dequal } from 'dequal';
import { first, isEmpty, isString, isUndefined } from 'lodash';
import { PageFilter } from './PageFilterInteractivityUtils';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- pre-existing pattern: static-only validation class
export class FilterValidation {
  private constructor() {
    // no-op
  }

  /**
   * AWFilter does not support "invert" shape, since we do not have a FE driven use case for it
   */
  private static compulsoryAwFilterConstraints = (filter: AWFilter): boolean =>
    filter.invert === false && filter.operator !== 'NOT_IN';

  private static isMultiSelectFilter = (filter: AWFilter): boolean =>
    FilterValidation.compulsoryAwFilterConstraints(filter) &&
    !isEmpty(filter.colTag) &&
    filter.operator === 'IN' &&
    !isEmpty(filter.values) &&
    isString(first(filter.values)) &&
    filter.valueType === 'VARCHAR' &&
    filter.startInclusive === true &&
    filter.endInclusive === false;

  private static isSingleSelectFilter = (filter: AWFilter): boolean =>
    FilterValidation.isMultiSelectFilter(filter) && filter.values.length === 1;

  private static isRegularSearchFilter = (filter: AWFilter): boolean => FilterValidation.isMultiSelectFilter(filter);

  private static isSliderFilter = (filter: AWFilter): boolean =>
    FilterValidation.compulsoryAwFilterConstraints(filter) &&
    !isEmpty(filter.colTag) &&
    isEmpty(filter.values) &&
    (filter.valueType === 'FLOAT' || filter.valueType === 'INT') &&
    (((filter.operator === 'GREATER_THAN' || filter.operator === 'LESS_THAN') && !isUndefined(filter.value)) ||
      (filter.operator === 'BETWEEN' &&
        !isUndefined(filter.start) &&
        !isUndefined(filter.end) &&
        filter.startInclusive === true &&
        filter.endInclusive === true));

  private static isBooleanListFilter = (filter: AWFilter): boolean =>
    FilterValidation.isSingleSelectFilter(filter) && first(filter.values) === 'Y';

  private static isEntitySearchFilter = (filter: AWFilter): boolean =>
    FilterValidation.compulsoryAwFilterConstraints(filter) &&
    !isEmpty(filter.colTag) &&
    filter.operator === 'SPACE_PADDED' &&
    filter.valueType === 'VARCHAR' &&
    filter.startInclusive === true &&
    filter.endInclusive === false &&
    !isEmpty(filter.values) &&
    isReactSelectOptions(first(filter.values));

  private static simpleValidationMapping: Record<SimpleValidationScenario, (filter: AWFilter) => boolean> = {
    IS_SLIDER_AWFILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isSliderFilter(filter);
    },
    IS_SINGLE_SELECT_AWFILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isSingleSelectFilter(filter);
    },
    IS_MULTI_SELECT_AWFILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isMultiSelectFilter(filter);
    },
    IS_BOOLEAN_LIST_AWFILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isBooleanListFilter(filter);
    },
    IS_ENTITY_SEARCH_AWFILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isEntitySearchFilter(filter);
    },
    IS_REGULAR_SEARCH_AW_FILTER: function (filter: AWFilter): boolean {
      return FilterValidation.isRegularSearchFilter(filter);
    },
    IS_SELECT_TYPE_AWFILTER: function (filter: AWFilter): boolean {
      return (
        FilterValidation.isMultiSelectFilter(filter) ||
        FilterValidation.isSingleSelectFilter(filter) ||
        FilterValidation.isBooleanListFilter(filter) ||
        FilterValidation.isRegularSearchFilter(filter)
      );
    },
  };

  static simpleValidation = (validationScenario: SimpleValidationScenario, filter: AWFilter): boolean =>
    FilterValidation.simpleValidationMapping[validationScenario](filter);
}

export type SimpleValidationScenario =
  | 'IS_SLIDER_AWFILTER'
  | 'IS_SELECT_TYPE_AWFILTER'
  | 'IS_SINGLE_SELECT_AWFILTER'
  | 'IS_MULTI_SELECT_AWFILTER'
  | 'IS_BOOLEAN_LIST_AWFILTER'
  | 'IS_ENTITY_SEARCH_AWFILTER'
  | 'IS_REGULAR_SEARCH_AW_FILTER';

/**
 * This function simply convert PageFilter to an AWFilter
 * No validation is done within this function
 */
export const convertPageFilterToAWFilter = (
  pageFilter: PageFilter,
  filterableAttribute: AWAdvisorFilterableAttribute
): AWFilter => {
  const { type } = filterableAttribute;
  if (
    type === 'single-select' ||
    type === 'multi-select' ||
    type === 'boolean-list' ||
    type === 'prefix-search' ||
    type === 'quick-prefix-search' ||
    type === 'general-search' ||
    type === 'quick-general-search'
  ) {
    return {
      colTag: pageFilter.colTag,
      operator: 'IN',
      values: pageFilter.values,
      valueType: 'VARCHAR',
      invert: false,
      startInclusive: true,
      endInclusive: false,
    };
  } else if (type === 'slider' || type === 'range') {
    if (pageFilter.values.length !== 2) {
      throw new Error('Slider type filter can only support 2 values at the moment (between)');
    }
    return {
      colTag: pageFilter.colTag,
      operator: 'BETWEEN',
      start: pageFilter.values[0],
      end: pageFilter.values[1],
      valueType: 'FLOAT',
      values: [],
      invert: false,
      startInclusive: true,
      endInclusive: true,
    };
  }
  throw new Error('Invalid filterable attribute type for page filter conversion');
};

/**
 * @deprecated Do not use this function
 */
export const filtersConsideredEqual = (filterA: AWFilter | Filter, filterB: AWFilter | Filter): boolean =>
  filterA.colTag === filterB.colTag &&
  filterA.operator === filterB.operator &&
  (!isUndefined(filterA.values)
    ? dequal(filterA.values, filterB.values) // values take precedence over value
    : filterA.value === filterB.value);
